import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';

const QuickActionsPanel = ({ userRole, stats, onCityUpdate }) => {
  const navigate = useNavigate();
  const [citySettings, setCitySettings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userRole === 'super_admin') {
      loadCitySettings();
    }
  }, [userRole]);

  const loadCitySettings = async () => {
    try {
      const settings = await adminService.getCitySettings();
      setCitySettings(settings);
    } catch (error) {
      console.error('Error loading city settings:', error);
    }
  };

  const handleCityToggle = async (city, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'close' : 'open'} registration for ${city}?`)) {
      return;
    }

    setLoading(true);
    try {
      await adminService.updateCitySettings(city, !currentStatus);
      await loadCitySettings();
      if (onCityUpdate) {
        onCityUpdate();
      }
    } catch (error) {
      console.error('Error updating city settings:', error);
      alert(`Failed to update city settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickActionClick = (actionId) => {
    switch (actionId) {
      case 'pending-reviews':
        navigate('/admin-dashboard', { state: { filterStatus: 'submitted' } });
        break;
      case 'recent-submissions':
        navigate('/admin-dashboard');
        break;
      case 'communication-history':
        navigate('/admin-communication-center');
        break;
      case 'city-management':
        // Scroll to city management section (already visible)
        break;
      default:
        break;
    }
  };
  const actions = [
    {
      id: 'pending-reviews',
      title: 'Pending Reviews',
      count: stats?.pendingReviews,
      icon: 'Clock',
      color: 'var(--color-warning)',
      roles: ['super_admin', 'reviewer']
    },
    {
      id: 'recent-submissions',
      title: 'Recent Submissions',
      count: stats?.recentSubmissions,
      icon: 'FileText',
      color: 'var(--color-primary)',
      roles: ['super_admin', 'reviewer', 'data_user']
    },
    {
      id: 'communication-history',
      title: 'Messages Sent',
      count: stats?.messagesSent,
      icon: 'MessageSquare',
      color: 'var(--color-accent)',
      roles: ['super_admin', 'comms_user']
    },
    {
      id: 'city-management',
      title: 'Active Cities',
      count: stats?.activeCities,
      icon: 'MapPin',
      color: 'var(--color-success)',
      roles: ['super_admin']
    }
  ];

  const filteredActions = actions?.filter(action => action?.roles?.includes(userRole));

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
      {/* Hide Quick Actions cards - keep only City Management */}
      {false && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredActions?.map((action) => (
            <button
              key={action?.id}
              onClick={() => handleQuickActionClick(action.id)}
              className="flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 text-left touch-target"
            >
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${action?.color}15` }}
              >
                <Icon name={action?.icon} size={24} color={action?.color} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-1">{action?.title}</p>
                <p className="text-2xl font-bold text-foreground">{action?.count}</p>
              </div>
              <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
            </button>
          ))}
        </div>
      )}
      {userRole === 'super_admin' && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">City Management</h4>
          <div className="space-y-2">
            {citySettings.length > 0 ? (
              citySettings.map((city) => (
                <div key={city.city} className="flex items-center justify-between p-2 rounded hover:bg-muted/50">
                  <span className="text-sm text-foreground">{city.city}</span>
                  <Button 
                    variant={city.is_open ? "success" : "outline"} 
                    size="xs"
                    onClick={() => handleCityToggle(city.city, city.is_open)}
                    disabled={loading}
                  >
                    {city.is_open ? 'Open' : 'Closed'}
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Loading city settings...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActionsPanel;